import { useCallback, useSyncExternalStore } from 'react';
import { createRecordingAudio } from '../utils/recordingAudio';

/**
 * Records ONLY the current browser tab (the app), never the whole screen.
 *
 * The recorder lives in a module-level singleton, NOT in component state, so it
 * keeps running when the Header/DashboardLayout remounts on route changes.
 * Only a full page reload ends a recording early; we warn via beforeunload.
 *
 * - Chrome/Edge 109+: preferCurrentTab pre-selects this tab in the picker and
 *   monitor/window options are removed, so the user cannot choose the full screen.
 * - Other browsers show their native picker; we verify the chosen surface is a
 *   browser tab and abort if the user picked a monitor or window.
 *
 * Returns { status, error, elapsedMs, remainingMs, bytesRecorded, blob, stopReason, start, stop, reset }.
 * Recording auto-stops at MAX_RECORDING_MS or when the file nears MAX_UPLOAD_BYTES.
 * status: 'idle' | 'starting' | 'recording' | 'stopped' | 'error'
 */

const TIMESLICE_MS = 1000;

/** Server rejects uploads above this (RECORDINGS_MAX_BYTES on the lead service). */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500 MB
/** Target bitrate; with a mostly static UI VP9 usually produces well under this. */
export const VIDEO_BITS_PER_SECOND = 1_200_000;
export const AUDIO_BITS_PER_SECOND = 128_000;
/**
 * Capture caps. Retina/4K tabs are captured at full device pixels by default, which makes the
 * encoder blow past the bitrate target. 1080p at 15 fps keeps UI text readable and the size sane.
 */
export const CAPTURE_MAX_WIDTH = 1920;
export const CAPTURE_MAX_HEIGHT = 1080;
export const CAPTURE_FRAME_RATE = 15;
/** Video + audio targets total about 498 MB over 50 minutes; the size guard also caps output. */
export const MAX_RECORDING_MS = 50 * 60 * 1000; // 50 minutes

const pickMimeType = () => {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  if (typeof MediaRecorder === 'undefined') return '';
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

export const isScreenRecordingSupported = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getDisplayMedia &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== 'undefined';

// ---------------------------------------------------------------------------
// Singleton store (survives component unmount / route changes)
// ---------------------------------------------------------------------------
const initialSnapshot = {
  status: 'idle',
  publicSession: false,
  publicLinkToken: null,
  error: null,
  elapsedMs: 0,
  remainingMs: MAX_RECORDING_MS,
  bytesRecorded: 0,
  blob: null,
  /** 'user' | 'time' | 'size' | 'browser' - why the last recording stopped */
  stopReason: null,
};

const store = {
  snapshot: initialSnapshot,
  listeners: new Set(),
  recorder: null,
  stream: null,
  releaseAudio: null,
  chunks: [],
  bytes: 0,
  startedAt: 0,
  timer: null,
  stopResolve: null,
  pendingReason: 'user',
};

const emit = (patch) => {
  store.snapshot = { ...store.snapshot, ...patch };
  store.listeners.forEach((l) => l());
};

const subscribe = (listener) => {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
};

const getSnapshot = () => store.snapshot;

const warnBeforeUnload = (evt) => {
  evt.preventDefault();
  evt.returnValue = 'A screen recording is in progress or not yet saved and will be lost if you leave.';
  return evt.returnValue;
};

const releaseStream = () => {
  store.stream?.getTracks().forEach((t) => t.stop());
  store.stream = null;
  store.releaseAudio?.();
  store.releaseAudio = null;
  if (store.timer) {
    clearInterval(store.timer);
    store.timer = null;
  }
};

const fail = (msg, status = 'error') => {
  emit({ error: msg, status });
  return new Error(msg);
};

async function startRecording({ publicSession = false, publicLinkToken = null } = {}) {
  if (!isScreenRecordingSupported()) {
    throw fail('Screen recording is not supported in this browser.');
  }
  if (store.snapshot.status === 'starting' || store.recorder?.state === 'recording') return;

  emit({ publicSession, publicLinkToken, status: 'starting', error: null, blob: null, stopReason: null, bytesRecorded: 0, remainingMs: MAX_RECORDING_MS });
  store.chunks = [];
  store.bytes = 0;
  store.pendingReason = 'user';

  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'browser',
        width: { max: CAPTURE_MAX_WIDTH },
        height: { max: CAPTURE_MAX_HEIGHT },
        frameRate: { ideal: CAPTURE_FRAME_RATE, max: CAPTURE_FRAME_RATE },
      },
      audio: true,
      // Chromium-only hints: pre-select this tab, hide monitor/window choices.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
      systemAudio: 'exclude',
    });
  } catch (e) {
    const msg = e?.name === 'NotAllowedError' ? 'Recording was cancelled.' : e?.message || 'Could not start recording.';
    throw fail(msg, 'idle');
  }

  const [track] = stream.getVideoTracks();
  const surface = track?.getSettings?.().displaySurface;
  if (!track || (surface && surface !== 'browser')) {
    stream.getTracks().forEach((t) => t.stop());
    throw fail("Please select the app's browser tab, not the entire screen or a window.");
  }

  // Some browsers ignore caps passed to getDisplayMedia; re-apply them on the track.
  try {
    await track.applyConstraints({
      width: { max: CAPTURE_MAX_WIDTH },
      height: { max: CAPTURE_MAX_HEIGHT },
      frameRate: { ideal: CAPTURE_FRAME_RATE, max: CAPTURE_FRAME_RATE },
    });
  } catch {
    // Non-fatal: recording still works, just at native resolution.
  }

  const mimeType = pickMimeType();
  let recorder;
  store.stream = stream;
  try {
    const audio = await createRecordingAudio(stream);
    store.releaseAudio = audio.release;
    if (track.readyState === 'ended') {
      throw new Error('Tab sharing ended before recording could start. Please try again.');
    }
    recorder = new MediaRecorder(audio.stream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    });
  } catch (e) {
    releaseStream();
    throw fail(e?.message || 'MediaRecorder could not be created.');
  }

  const stopWith = (reason) => {
    if (recorder.state === 'inactive') return;
    store.pendingReason = reason;
    recorder.stop();
  };

  recorder.ondataavailable = (evt) => {
    if (!evt.data || evt.data.size === 0) return;
    store.chunks.push(evt.data);
    store.bytes += evt.data.size;
    emit({ bytesRecorded: store.bytes });
    // Safety net: never let the file grow past what the server accepts.
    if (store.bytes >= MAX_UPLOAD_BYTES * 0.97) stopWith('size');
  };
  recorder.onerror = (evt) => {
    emit({ error: evt?.error?.message || 'Recording error.', status: 'error' });
  };
  recorder.onstop = () => {
    const type = recorder.mimeType || mimeType || 'video/webm';
    const out = new Blob(store.chunks, { type });
    releaseStream();
    emit({
      blob: out,
      elapsedMs: Date.now() - store.startedAt,
      bytesRecorded: out.size,
      status: 'stopped',
      stopReason: store.pendingReason,
    });
    if (store.stopResolve) {
      store.stopResolve(out);
      store.stopResolve = null;
    }
  };

  // User pressed the browser's own "Stop sharing" control.
  track.addEventListener('ended', () => stopWith('browser'));

  store.stream = stream;
  store.recorder = recorder;
  store.startedAt = Date.now();
  store.timer = setInterval(() => {
    const elapsedMs = Date.now() - store.startedAt;
    const remainingMs = Math.max(0, MAX_RECORDING_MS - elapsedMs);
    emit({ elapsedMs, remainingMs });
    if (remainingMs === 0) stopWith('time');
  }, 500);
  window.addEventListener('beforeunload', warnBeforeUnload);

  try {
    recorder.start(TIMESLICE_MS);
  } catch (e) {
    releaseStream();
    window.removeEventListener('beforeunload', warnBeforeUnload);
    store.recorder = null;
    throw fail(e?.message || 'Could not start recording.');
  }
  emit({ elapsedMs: 0, remainingMs: MAX_RECORDING_MS, status: 'recording' });

  if (process.env.NODE_ENV !== 'production') {
    const { width, height, frameRate } = track.getSettings();
    // eslint-disable-next-line no-console
    console.debug('[screen-recorder] started', {
      mimeType: recorder.mimeType,
      videoBitsPerSecond: recorder.videoBitsPerSecond,
      capture: `${width}x${height}@${frameRate}fps`,
    });
  }
}

/** Stops recording and resolves with the final Blob. */
function stopRecording() {
  const recorder = store.recorder;
  if (!recorder || recorder.state === 'inactive') {
    return Promise.resolve(store.snapshot.blob);
  }
  return new Promise((resolve) => {
    store.stopResolve = resolve;
    recorder.stop();
  });
}

function resetRecording() {
  if (store.recorder && store.recorder.state !== 'inactive') {
    store.recorder.onstop = null;
    store.recorder.stop();
  }
  releaseStream();
  window.removeEventListener('beforeunload', warnBeforeUnload);
  store.recorder = null;
  store.chunks = [];
  store.bytes = 0;
  store.stopResolve = null;
  emit(initialSnapshot);
}

// ---------------------------------------------------------------------------
// Hook: thin subscription over the singleton
// ---------------------------------------------------------------------------
export default function useScreenRecorder() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const start = useCallback((options) => startRecording(options), []);
  const stop = useCallback(() => stopRecording(), []);
  const reset = useCallback(() => resetRecording(), []);

  return { ...snapshot, start, stop, reset };
}
